import type { Deal, DealStage, MainTab } from '@/types'

export const stageConfig: Record<DealStage, { label: string; color: string; defaultProbability?: number }> = {
  qualificado: { label: 'Qualificado (SQL)', color: '#3B82F6', defaultProbability: 20 },
  reuniao_agendada: { label: 'Reunião Agendada', color: '#8B5CF6', defaultProbability: 40 },
  reuniao_realizada: { label: 'Reunião Realizada', color: '#A855F7', defaultProbability: 60 },
  proposta_elaboracao: { label: 'Proposta em Elaboração', color: '#F59E0B', defaultProbability: 70 },
  proposta_enviada: { label: 'Proposta Enviada', color: '#14BDBD', defaultProbability: 80 },
  teste_gratis: { label: '30 dias Grátis', color: '#0EA5E9', defaultProbability: 75 },
  renegociacao: { label: 'Em Renegociação', color: '#E35933', defaultProbability: 65 },
  ajustar_proposta: { label: 'Ajustar Proposta', color: '#D97706', defaultProbability: 78 },
  gerar_contrato: { label: 'Gerar Contrato', color: '#2563EB', defaultProbability: 85 },
  contrato_enviado: { label: 'Contrato Enviado', color: '#4F46E5', defaultProbability: 90 },
  handover: { label: 'Handover', color: '#0D9488', defaultProbability: 95 },
  ganho: { label: 'Contrato Assinado', color: '#10B981', defaultProbability: 100 },
  perdido: { label: 'Cancelado/Perdido', color: '#EF4444', defaultProbability: 0 },
  pausado: { label: 'Pausado', color: '#6B7280', defaultProbability: 10 },
  fechamento_futuro: { label: 'Negociação Futura', color: '#6366F1', defaultProbability: 30 },
}

export const stageOrder: DealStage[] = [
  'qualificado',
  'reuniao_agendada',
  'reuniao_realizada',
  'proposta_elaboracao',
  'proposta_enviada',
  'teste_gratis',
  'renegociacao',
  'ajustar_proposta',
  'gerar_contrato',
  'contrato_enviado',
  'handover',
  'ganho',
  'perdido',
  'pausado',
  'fechamento_futuro',
]

/** Etapas permitidas ao criar um deal manualmente */
export const creatableDealStages: DealStage[] = [
  'qualificado',
  'reuniao_agendada',
  'reuniao_realizada',
  'proposta_elaboracao',
]

export const activeStages: DealStage[] = [
  'qualificado',
  'reuniao_agendada',
  'reuniao_realizada',
  'proposta_elaboracao',
  'proposta_enviada',
  'teste_gratis',
  'renegociacao',
  'ajustar_proposta',
  'gerar_contrato',
  'contrato_enviado',
  'handover',
]

export const closedStages: DealStage[] = ['ganho', 'perdido']

export const futureStages: DealStage[] = ['fechamento_futuro', 'pausado']

export function dealMatchesMainTab(deal: Pick<Deal, 'etapa'>, mainTab: MainTab): boolean {
  if (mainTab === 'closed') return closedStages.includes(deal.etapa)
  if (mainTab === 'future') return futureStages.includes(deal.etapa)
  if (mainTab === 'active') return activeStages.includes(deal.etapa)
  return true
}

export function dealMatchesSearch(deal: Deal, searchTerm: string): boolean {
  const term = searchTerm.trim().toLowerCase()
  if (!term) return true

  return [deal.nome, deal.empresaNome, deal.closerNome, deal.servico]
    .filter(Boolean)
    .some((value) => value!.toLowerCase().includes(term))
}

export function getStageLabel(stage: DealStage): string {
  return stageConfig[stage]?.label ?? stage
}

export function getStageColor(stage: DealStage): string {
  return stageConfig[stage]?.color ?? '#888'
}

export const proposalVisibleStages: DealStage[] = [
  'proposta_elaboracao',
  'proposta_enviada',
  'renegociacao',
  'ajustar_proposta',
  'gerar_contrato',
  'contrato_enviado',
  'ganho',
]

export function isProposalTabVisible(etapa: DealStage): boolean {
  return proposalVisibleStages.includes(etapa)
}

export const contractTabVisibleStages: DealStage[] = [
  'gerar_contrato',
  'contrato_enviado',
  'handover',
  'ganho',
]

export function isContractTabVisible(etapa: DealStage): boolean {
  return contractTabVisibleStages.includes(etapa)
}

export function getEffectiveProbability(deal: Pick<Deal, 'etapa' | 'probabilidade'>): number {
  const stageDefaultStages: DealStage[] = ['qualificado', 'reuniao_agendada', 'reuniao_realizada']
  if (stageDefaultStages.includes(deal.etapa)) {
    return stageConfig[deal.etapa]?.defaultProbability ?? deal.probabilidade
  }
  return deal.probabilidade
}

export function usesStageDefaultProbability(etapa: DealStage): boolean {
  return etapa === 'qualificado' || etapa === 'reuniao_agendada' || etapa === 'reuniao_realizada'
}
